import React, { useRef, useState } from "react";
import GradeSettingContents, {
  type GradeSettingContentsHandle,
} from "./GradeSettingContents";
import useGradeConfig from "@hooks/useGradeConfig";
import useNurses from "@hooks/useNurses";
import { useShiftsQuery } from "@/hooks/roster-craete/useShiftsQuery";
import { useIsAdmin } from "@hooks/useAuth";
import { ConfirmationDialog } from "@/components/modal/ConfirmationDialog";
import { ManagementDialog } from "@/components/modal/ManagementDialog";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import type { NurseResponse } from "@vTypes/nurse-type";

interface GradeSettingModalProps {
  onClose: () => void;
  selectedGroupId?: string | null;
  isAdmin: boolean;
  nurseDatas?: NurseResponse[];
}

const GradeSettingModal: React.FC<GradeSettingModalProps> = ({
  onClose,
  selectedGroupId,
  nurseDatas,
}) => {
  const isAdmin = useIsAdmin();
  const gid = isAdmin ? (selectedGroupId ?? "") : "";

  const contentsRef = useRef<GradeSettingContentsHandle>(null);
  const [savePending, setSavePending] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [isExitConfirmOpen, setIsExitConfirmOpen] = useState(false);

  const { isLoading: isShiftsLoading } = useShiftsQuery(gid);
  const { isLoading } = useGradeConfig({ selectedGroupId: gid });
  const hasInjectedNurses = Array.isArray(nurseDatas);
  const { data: fallbackNurseDatas, isLoading: nurseLoading } = useNurses(gid, {
    enabled: !hasInjectedNurses,
  });
  const effectiveNurseDatas = nurseDatas ?? fallbackNurseDatas;

  const requestClose = () => {
    if (savePending) return;
    if (!isDirty) {
      onClose();
      return;
    }
    setIsExitConfirmOpen(true);
  };

  const closeWithoutSave = () => {
    setIsExitConfirmOpen(false);
    onClose();
  };

  const saveAndClose = () => {
    contentsRef.current?.triggerSaveDirect();
  };

  return (
    <>
      <ConfirmationDialog
        open={isExitConfirmOpen}
        onClose={closeWithoutSave}
        onConfirm={saveAndClose}
        isPending={savePending}
        pendingText="저장 중..."
        heading="변경사항을 저장하시겠습니까?"
        title="저장하지 않고 닫으면 변경사항이 사라집니다."
        confirmText="저장"
        cancelText="저장하지 않고 닫기"
      />
      <ManagementDialog
        onClose={requestClose}
        title="Grade 설정"
        widthClassName="max-w-none xl:w-[90vw] xl:max-w-[calc(100vw-300px)]"
        isBusy={savePending || isLoading || isShiftsLoading || nurseLoading}
        footer={
          <>
            <Button type="button" variant="outline" className="rounded-full" disabled={savePending} onClick={requestClose}>
              닫기
            </Button>
            <Button type="button" className="rounded-full bg-sig text-white hover:bg-sig/90" disabled={savePending || isLoading || isShiftsLoading || nurseLoading} onClick={() => contentsRef.current?.triggerSave()}>
              {savePending && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
              저장
            </Button>
          </>
        }
      >
        {isLoading || isShiftsLoading || nurseLoading ? (
          <div className="flex min-h-60 items-center justify-center text-muted-foreground" role="status" aria-label="Grade 설정 불러오는 중">
            <Loader2 className="size-6 animate-spin" aria-hidden="true" />
          </div>
        ) : (
          <GradeSettingContents
            ref={contentsRef}
            onClose={onClose}
            selectedGroupId={gid}
            nurseDatas={effectiveNurseDatas}
            onMutationPendingChange={setSavePending}
            onDirtyChange={setIsDirty}
            hideTitle
          />
        )}
      </ManagementDialog>
    </>
  );
};

export default GradeSettingModal;
